import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Building2, CalendarDays, MessageSquare,
  Star, Image, Layers, BarChart3, ScrollText, Settings, LogOut, Shield, Sun, Moon, KeyRound
} from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';
import { usePermissions } from './usePermissions';
import { useTheme } from '../ThemeContext';
import { Toaster } from 'sonner';

const NAV_BASE = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true, perm: 'dashboard.view' },
  { to: '/admin/users', icon: Users, label: 'Users', perm: 'users.view' },
  { to: '/admin/organizers', icon: Building2, label: 'Organizers', perm: 'organizers.view' },
  { to: '/admin/events', icon: CalendarDays, label: 'Events', perm: 'events.view' },
  { to: '/admin/enquiries', icon: MessageSquare, label: 'Enquiries', perm: 'enquiries.view' },
  { to: '/admin/reviews', icon: Star, label: 'Reviews', perm: 'reviews.view' },
  { to: '/admin/banners', icon: Image, label: 'Banners', perm: 'banners.view' },
  { to: '/admin/masters', icon: Layers, label: 'Masters', perm: 'masters.view' },
  { to: '/admin/reports', icon: BarChart3, label: 'Reports', perm: 'reports.view' },
  { to: '/admin/logs', icon: ScrollText, label: 'Logs', perm: 'logs.view' },
  { to: '/admin/settings', icon: Settings, label: 'Settings', perm: 'settings.view' },
  { to: '/admin/user-privileges', icon: KeyRound, label: 'User Privileges', perm: 'user_privileges.view' },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const { has } = usePermissions();
  const navigate = useNavigate();
  const { resolved, setTheme } = useTheme();
  const toggleTheme = () => setTheme(resolved === 'dark' ? 'light' : 'dark');

  const NAV = NAV_BASE.filter(item => !item.perm || has(item.perm));

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      <Toaster position="top-center" richColors closeButton />

      {/* Sidebar */}
      <aside className="admin-sidebar hidden lg:flex flex-col w-60 bg-gray-50 border-r border-gray-200 flex-shrink-0 fixed inset-y-0 left-0 z-40">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-gray-200">
          <img src="/radius-icon.png" alt="RADIUS" className="w-8 h-8 rounded-lg object-cover" />
          <span className="text-sm font-black tracking-widest text-gray-900">RADIUS</span>
          <span className="text-[9px] font-bold bg-pink-50 text-brand-magenta px-1.5 py-0.5 rounded">ADMIN</span>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-semibold transition-colors ${
                  isActive
                    ? 'bg-brand-magenta text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-gray-200 space-y-1">
          <button
            data-testid="admin-theme-toggle"
            onClick={toggleTheme}
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors w-full"
          >
            {resolved === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {resolved === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <button
            data-testid="admin-sidebar-logout"
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors w-full"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col lg:ml-60">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 shadow-sm">
          {/* Mobile toggle - simplified with brand */}
          <div className="lg:hidden flex items-center gap-2">
            <img src="/radius-icon.png" alt="RADIUS" className="w-7 h-7 rounded-md object-cover" />
            <span className="text-sm font-black tracking-wider text-gray-900">ADMIN</span>
          </div>
          <div className="hidden lg:block" />

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg">
              <Shield className="w-3.5 h-3.5 text-brand-magenta" />
              <span className="text-xs font-bold text-gray-700">{admin?.email}</span>
            </div>
            <button
              data-testid="admin-topbar-logout"
              onClick={handleLogout}
              className="lg:hidden w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </div>
        </header>

        {/* Mobile bottom nav */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-around h-14 overflow-x-auto no-scrollbar px-1">
            {NAV.slice(0, 6).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 py-1 px-2 rounded text-[9px] font-bold transition-colors ${
                    isActive ? 'text-brand-magenta' : 'text-gray-400'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="flex-1 p-4 lg:p-6 pb-20 lg:pb-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
